import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { omit } from 'es-toolkit';
import { validate } from 'validate.js';

import { Alert, Box, Form, Heading, List, Modal, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  FeeApplicableLevelType,
  FeeCalculationType,
  FeeRequirementType_values,
  FeeTimingSubType_values,
  FeeType,
  FeeTypeGrouping,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_create,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import accessUtils from '../../../utils/accessUtils';
import { removeErrorKey } from '../../../utils/formValidation';

import {
  emitClickCancelFeeEditEvent,
  emitClickSaveFeeEvent,
  EVENT_IDS,
} from '../../../analytics/events/multiFamilyCostsAndFees';
import costsAndFeesConstraints from '../../../validation/costsandFeesConstraints';
import InlineStaticAlert from '../../Alerts/InlineStaticAlert';
import { useAuth } from '../../AuthProvider';
import FeeAmountField from '../../CostsAndFeesFields/FeeAmountField';
import FeeComboboxField from '../../CostsAndFeesFields/FeeComboboxField';
import FeeDropdownSelectField from '../../CostsAndFeesFields/FeeDropdownSelectField';
import FeeInputField from '../../CostsAndFeesFields/FeeInputField';
import FeeRadioGroupField from '../../CostsAndFeesFields/FeeRadioGroupField';
import MaxOccurrencesField from '../../CostsAndFeesFields/MaxOccurrencesField';
import SteppedFeeField from '../../CostsAndFeesFields/SteppedFeeField';
import ModalFooter from '../ModalFooter';

import type {
  CreateMultifamilyFeesRequestDto,
  FeeCalculationDictionaryItemDisplayDto,
  FeeCategoryDictionaryItemDto,
  FeeDictionaryItemDisplayDto,
  FeePaymentFrequencyDictionaryItemDto,
  FeeRefundableType,
  FeeRequirementType,
  FeeTimingSubType,
  ModelDictionaryItemDto,
  MultifamilyFeeDto,
  UpdateMultifamilyFeesRequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  onClose: ({ isSaving }: { isSaving: boolean }) => void;
  feeToEdit?: MultifamilyFeeDto | null;
};

const formatCreateOrUpdateErrors = (generalMessage: string, errors: Array<{ message: string }>) => (
  <>
    <Paragraph>{generalMessage}</Paragraph>
    <List.Root>
      {errors.map((error) => (
        <List.Item key={error.message}>{error.message}</List.Item>
      ))}
    </List.Root>
  </>
);

const MultifamilyCostsAndFeesModal = ({ onClose, feeToEdit = null }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const topRef = useRef<HTMLDivElement>(null);

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee;
  const isMultifamily = listingDetails?.isMultifamily;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;

  const initialFeeAmountMax =
    feeToEdit?.feeAmountMax &&
    (feeToEdit?.feeCalculationType === FeeCalculationType.RANGE ||
      feeToEdit?.feeCalculationType === FeeCalculationType.PER_ITEM_RANGE)
      ? feeToEdit.feeAmountMax
      : 0;
  const [feeAmountMax, setFeeAmountMax] = useState(initialFeeAmountMax);
  const [feeAmountMin, setFeeAmountMin] = useState(feeToEdit?.feeAmountMin || 0);
  const [steppedFeeAmounts, setSteppedFeeAmounts] = useState<Array<number>>(
    feeToEdit?.amountStepped && feeToEdit.amountStepped.length >= 2 ? feeToEdit.amountStepped : [0, 0],
  );
  const [feeApplicableLevelType, setFeeApplicableLevelType] = useState(feeToEdit?.feeApplicableLevelType || '');
  const [feeCategory, setFeeCategory] = useState(feeToEdit?.feeCategory || '');
  const [feeDescription, setFeeDescription] = useState(feeToEdit?.feeDescription || '');
  const [feeType, setFeeType] = useState(feeToEdit?.feeType || '');
  const [feeName, setFeeName] = useState(feeToEdit?.feeName || '');
  const [feePaymentFrequencyType, setFeePaymentFrequencyType] = useState(feeToEdit?.feePaymentFrequencyType || '');
  const [feeRefundableType, setFeeRefundableType] = useState(feeToEdit?.feeRefundableType || '');
  const [feeRequirementType, setFeeRequirementType] = useState(feeToEdit?.feeRequirementType || '');
  const [feeFormat, setFeeFormat] = useState(feeToEdit?.feeCalculationType || '');
  const [floorplans, setFloorplans] = useState(feeToEdit?.floorplan || ([] as Array<string>));
  const [units, setUnits] = useState(feeToEdit?.unit || ([] as Array<string>));
  const [maxOccurrences, setMaxOccurrences] = useState(feeToEdit?.maxOccurrences || 0);

  const [feeErrors, setFeeErrors] = useState<Record<string, string>>({});

  const [feeCreateOrUpdateError, setFeeCreateOrUpdateError] = useState<React.ReactNode | null>(null);

  const { data } = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get(
    {
      listingAlias,
    },
    {
      enabled: isMultifamily,
      meta: {
        tags: ['mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'get multifamily costs and fees' },
      },
    },
  );

  // Initialize validation constraints when data is loaded
  useEffect(() => {
    if (data?.dictionaryItems) {
      costsAndFeesConstraints.getcostsAndFeesConstraints(data.dictionaryItems);
    }
  }, [data?.dictionaryItems]);

  const showFeeError = (baseMessage: string, errors?: Array<{ message: string }>) => {
    if (errors?.length) {
      setFeeCreateOrUpdateError(formatCreateOrUpdateErrors(`${baseMessage} Error details:`, errors));
    } else {
      setFeeCreateOrUpdateError(baseMessage);
    }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const { mutate: createFee, isPending: isCreatingFee } =
    useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_create({
      onSuccess: (response) => {
        const responseErrors = response.errors as Array<{ message: string }>;
        if (responseErrors?.length) {
          showFeeError('Your new fee couldn’t be added. Try again.', responseErrors);
          return;
        }

        onClose({ isSaving: true });
        enqueueToast(<Toast tone="success">{feeToEdit ? 'Fee updated' : 'Fee added'}</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to create multifamily fee for listingAlias: ${listingAlias}`,
            details: 'Create multifamily fee',
          },
        });

        const { errors } = DatarouterError.getErrorDetails(err);
        showFeeError('Fee couldn’t be added. Try again.', errors);
      },
      meta: {
        invalidates: ['listingRecommendations', 'mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'create multifamily fee' },
      },
    });

  const { mutate: updateFee, isPending: isUpdatingFee } =
    useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_update({
      onSuccess: (response) => {
        const responseErrors = response.errors as Array<{ message: string }>;
        if (responseErrors?.length) {
          showFeeError('Changes couldn’t be saved. Try again.', responseErrors);
          return;
        }

        onClose({ isSaving: true });
        enqueueToast(<Toast tone="success">{feeToEdit ? 'Fee updated' : 'Fee added'}</Toast>);
      },
      onError: (err: unknown) => {
        logError({
          error: err,
          context: {
            message: `Failed to update multifamily fee for listingAlias: ${listingAlias}, feeGroupId: ${feeToEdit?.feeGroupId}`,
            details: 'Update multifamily fee',
          },
        });

        const { errors } = DatarouterError.getErrorDetails(err);
        showFeeError('Your changes couldn’t be saved. Try again.', errors);
      },
      meta: {
        invalidates: ['listingRecommendations', 'mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'update multifamily fee' },
      },
    });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const shouldRenderInlineStaticAlert =
    canAccessTotalMonthlyPriceWithFee &&
    displayTotalMonthlyPriceWithFee &&
    feePaymentFrequencyType === FeeTimingSubType_values[8] &&
    feeRequirementType === FeeRequirementType_values[2];

  const validateField = (fieldName: string, fieldValue: string | number) => {
    let error = '';
    let feeErrorObj = feeErrors;
    const maxGreaterThanMinErrorMessage = 'Maximum amount must be greater than minimum amount';

    // if feeFormat was changed, clear out the feeAmount errors since those values got cleared out
    if (fieldName === 'feeFormat') {
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: '',
        feeAmountMax: '',
        feeAmountMinFlat: '',
        feeAmountPercent: '',
        feeAmountMultiple: '',
        steppedFeeAmounts: '',
        steppedFeeAmount1: '',
        steppedFeeAmount2: '',
      };
    }
    // special validation for fee format: percentage of security deposit
    if (
      fieldName === 'feeFormat' &&
      (fieldValue === FeeCalculationType.PERCENTAGE_OF_SECURITY_DEPOSIT ||
        fieldValue === 'Percentage of security deposit')
    ) {
      // Check if the listing has a security deposit before allowing this fee format
      // need to check for the enum and the display value because `showLabelForValue` setting in constellation
      // is causing the value to get validated using the display value on blur.
      const listingHasNoSecurityDeposit =
        !data?.existingFees?.SECURITY_DEPOSIT ||
        (data?.existingFees?.SECURITY_DEPOSIT &&
          !data?.existingFees?.SECURITY_DEPOSIT.some(
            (fee: MultifamilyFeeDto) => fee.feeType === FeeType.SECURITY_DEPOSIT,
          ));

      if (listingHasNoSecurityDeposit) {
        error = 'Set a security deposit amount to use this fee format';
      }
    } else if (
      feeFormat === FeeCalculationType.RANGE &&
      (fieldName === 'feeAmountMin' || fieldName === 'feeAmountMax')
    ) {
      // if editing amounts for feeFormat RANGE, clear out the error maxGreaterThanMinErrorMessage. will revalidate max>min for the current field below
      // keeping this separate so that if the fieldValue for amount fields have another error, it can get validated separately below
      feeErrorObj = {
        ...feeErrorObj,
        feeAmountMin: feeErrors.feeAmountMin === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMin,
        feeAmountMax: feeErrors.feeAmountMax === maxGreaterThanMinErrorMessage ? '' : feeErrors.feeAmountMax,
      };
      if (
        (fieldName === 'feeAmountMin' && feeAmountMax <= (fieldValue as number)) ||
        (fieldName === 'feeAmountMax' && feeAmountMin >= (fieldValue as number))
      ) {
        error = maxGreaterThanMinErrorMessage;
      } else {
        let errorMin = '';
        let errorMax = '';
        // need to validate min and max every time one of the two range fields changes
        // Use feeAmountMinRange constraint for range fees to get correct minValue (0.01 instead of 0)
        const constraintsForMin = costsAndFeesConstraints.values?.['feeAmountMinRange'];
        if (constraintsForMin) {
          const validationErrorMin = validate(
            { ['feeAmountMinRange']: feeAmountMin },
            { ['feeAmountMinRange']: constraintsForMin },
          )?.['feeAmountMinRange']?.[0];
          if (validationErrorMin) {
            errorMin = removeErrorKey('feeAmountMinRange', validationErrorMin);
          }
        }

        const constraintsForMax = costsAndFeesConstraints.values?.['feeAmountMax'];
        if (constraintsForMax) {
          const validationErrorMax = validate(
            { ['feeAmountMax']: feeAmountMax },
            { ['feeAmountMax']: constraintsForMax },
          )?.['feeAmountMax']?.[0];
          if (validationErrorMax) {
            errorMax = removeErrorKey('feeAmountMax', validationErrorMax);
          }
        }

        return setFeeErrors({
          ...feeErrorObj,
          feeAmountMin: errorMin,
          feeAmountMax: errorMax,
        });
      }
    } else if (feeFormat === FeeCalculationType.STEPPED && fieldName.startsWith('steppedFeeAmount')) {
      // For stepped fees, use the fieldValue parameter directly instead of state
      // This ensures we validate the actual value that was entered, not stale state
      if (fieldValue !== undefined) {
        const constraintsForField = costsAndFeesConstraints.values?.[fieldName];
        if (constraintsForField) {
          const validationError = validate({ [fieldName]: fieldValue }, { [fieldName]: constraintsForField })?.[
            fieldName
          ]?.[0];
          if (validationError) {
            const error = removeErrorKey(fieldName, validationError);
            return setFeeErrors({
              ...feeErrorObj,
              [fieldName]: error,
            });
          }
        }
      }

      // Clear any existing errors for this field if validation passes
      if (feeErrors[fieldName]) {
        const newErrors = { ...feeErrors };
        delete newErrors[fieldName];
        setFeeErrors(newErrors);
      }
    } else {
      const constraintsForField = costsAndFeesConstraints.values?.[fieldName];
      if (constraintsForField) {
        const validationError = validate({ [fieldName]: fieldValue }, { [fieldName]: constraintsForField })?.[
          fieldName
        ]?.[0];
        if (validationError) {
          error = removeErrorKey(fieldName, validationError);
        }
      }
    }
    setFeeErrors({
      ...feeErrorObj,
      [fieldName]: error,
    });
  };

  const handleFeeCategoryChange = (value: string) => {
    setFeeCategory(value);
    setFeeType('');
  };

  const handleFeeApplicableLevelTypeChange = (value: string) => {
    setFeeApplicableLevelType(value);
    setFloorplans([]);
    setUnits([]);
  };

  const handleFeeFormatChange = (value: string) => {
    setFeeFormat(value);

    // Clear stepped fee amounts when switching away from stepped fees
    if (feeFormat === FeeCalculationType.STEPPED && value !== FeeCalculationType.STEPPED) {
      setSteppedFeeAmounts([0, 0]);
    }

    // Clear regular fee amounts when switching to stepped fees
    if (value === FeeCalculationType.STEPPED) {
      setFeeAmountMin(0);
      setFeeAmountMax(0);
    } else {
      // For non-stepped fees, reset feeAmountMax to 0 (feeAmountMin stays as-is)
      setFeeAmountMax(0);
    }
  };

  const handleStepRemoved = (stepIndex: number) => {
    // Clear validation errors for the removed step
    const fieldName = `steppedFeeAmount${stepIndex + 1}`;
    if (feeErrors[fieldName]) {
      const newErrors = { ...feeErrors };
      delete newErrors[fieldName];
      setFeeErrors(newErrors);
    }
  };

  const canSubmit = useMemo<boolean>(() => {
    switch (true) {
      case feeApplicableLevelType === FeeApplicableLevelType.UNIT && !units.length:
        return false;
      case feeApplicableLevelType === FeeApplicableLevelType.FLOOR_PLAN && !floorplans.length:
        return false;
      case feeFormat === FeeCalculationType.RANGE && (!feeAmountMax || !feeAmountMin):
        return false;
      case feeFormat === FeeCalculationType.STEPPED &&
        (steppedFeeAmounts.length < 2 || steppedFeeAmounts[0] === 0 || steppedFeeAmounts[1] === 0):
        return false;
      case feeFormat !== FeeCalculationType.FLAT_FEE &&
        feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED &&
        feeFormat !== FeeCalculationType.STEPPED &&
        feeAmountMin === 0:
        return false;
      case feeCategory === FeeTypeGrouping.CUSTOM && !feeName:
        return false;
      case isNaN(feeAmountMin) ||
        isNaN(feeAmountMax) ||
        !feeApplicableLevelType ||
        !feeFormat ||
        !feeCategory ||
        (feeCategory !== FeeTypeGrouping.CUSTOM && !feeType) ||
        !feePaymentFrequencyType ||
        !feeRequirementType:
        return false;
      case Object.values(feeErrors).some((error) => error !== ''):
        return false;
      default:
        return true;
    }
  }, [
    feeAmountMin,
    feeAmountMax,
    steppedFeeAmounts,
    feeApplicableLevelType,
    feeFormat,
    feeCategory,
    feeType,
    feePaymentFrequencyType,
    feeName,
    feeRequirementType,
    floorplans,
    units,
    feeErrors,
  ]);

  const onSubmit = () => {
    const postData: CreateMultifamilyFeesRequestDto = {
      feeApplicableLevelType: feeApplicableLevelType as FeeApplicableLevelType,
      feeCalculationType: feeFormat as FeeCalculationType,
      feeCategory: feeCategory as FeeTypeGrouping,
      feeDescription: feeDescription,
      feeType: feeType as FeeType,
      feePaymentFrequencyType: feePaymentFrequencyType as FeeTimingSubType,
      feeRequirementType: feeRequirementType as FeeRequirementType,
      floorplan: floorplans,
      listingAlias,
      unit: units,
    };

    if (feeRefundableType !== '') {
      postData.feeRefundableType = feeRefundableType as FeeRefundableType;
    }

    if (feeDescription !== '') {
      postData.feeDescription = feeDescription;
    }

    if (feeCategory === FeeTypeGrouping.CUSTOM) {
      postData.feeType = FeeType.CUSTOM_FEE;
      postData.feeName = feeName;
    }

    if (feeFormat === FeeCalculationType.STEPPED) {
      // For stepped fees, we need to handle the stepped amounts differently
      // The API expects both the amountStepped array and the min/max values
      if (steppedFeeAmounts.length >= 2 && steppedFeeAmounts[0] > 0 && steppedFeeAmounts[1] > 0) {
        // Add the stepped fee amounts array
        postData.amountStepped = steppedFeeAmounts;

        // For stepped fees, omit min/max values as expected by backend
        delete postData.feeAmountMin;
        delete postData.feeAmountMax;
      }
    } else if (feeFormat !== FeeCalculationType.VARIABLE_USAGE_BASED) {
      postData.feeAmountMin = feeAmountMin;
      postData.feeAmountMax = feeAmountMax || feeAmountMin;
    }

    if (feeToEdit) {
      // For updates, always include maxOccurrences - send null if 0 to clear the value
      const updateData: UpdateMultifamilyFeesRequestDto = {
        ...postData,
        maxOccurrences: maxOccurrences > 0 ? maxOccurrences : (null as unknown as number),
        feeGroupId: feeToEdit.feeGroupId,
      };
      updateFee({ postData: updateData });
    } else {
      // For creates, only include maxOccurrences if > 0
      if (maxOccurrences > 0) {
        postData.maxOccurrences = maxOccurrences;
      }
      createFee({ postData });
    }

    if (listingDetails && user) {
      emitClickSaveFeeEvent({ currentProperty: listingDetails, user });
    }
  };

  const feeApplicableLevelEnums = useMemo(() => {
    let feeApplicableLevelTypes = { ...data?.dictionaryItems?.feeApplicableLevelType } as Record<
      string,
      FeeDictionaryItemDisplayDto
    >;
    if (!data?.dictionaryItems?.unit || Object.keys(data?.dictionaryItems?.unit).length === 0) {
      feeApplicableLevelTypes = omit(feeApplicableLevelTypes, [FeeApplicableLevelType.UNIT]);
    }
    if (!data?.dictionaryItems?.floorplan || Object.keys(data?.dictionaryItems?.floorplan).length === 0) {
      feeApplicableLevelTypes = omit(feeApplicableLevelTypes, [FeeApplicableLevelType.FLOOR_PLAN]);
    }
    return feeApplicableLevelTypes;
  }, [data?.dictionaryItems?.floorplan, data?.dictionaryItems?.unit]);

  const feeLevelApplicableTooltipContent = useMemo(() => {
    let tooltipContent = '';

    if (Object.keys(feeApplicableLevelEnums).length === 1) {
      tooltipContent =
        'Only property-level fees are available. To apply fees to a floor plan or unit, you’ll need to add one first.';
    } else if (
      Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.BUILDING) &&
      Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.FLOOR_PLAN) &&
      !Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.UNIT)
    ) {
      tooltipContent =
        'Only property and floor plan-level fees are available. To apply fees to a unit, you’ll need to add one first.';
    } else if (
      Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.BUILDING) &&
      Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.UNIT) &&
      !Object.keys(feeApplicableLevelEnums).includes(FeeApplicableLevelType.FLOOR_PLAN)
    ) {
      tooltipContent =
        'Only property and unit-level fees are available. To apply fees to a floor plan, you’ll need to add one first.';
    }

    return tooltipContent;
  }, [feeApplicableLevelEnums]);

  const feeCalculationTypes = useMemo(() => {
    if (!data?.dictionaryItems?.feeCalculationType) {
      return {};
    }

    return data.dictionaryItems.feeCalculationType;
  }, [data?.dictionaryItems?.feeCalculationType]);

  const handleCancelClick = () => {
    if (listingDetails && user) {
      emitClickCancelFeeEditEvent({ currentProperty: listingDetails, user });
    }
    onClose({ isSaving: false });
  };

  return (
    <Modal
      open={true}
      onOpenChange={() => onClose({ isSaving: false })}
      header={<Heading level={2}>{feeToEdit ? 'Edit a fee' : 'Add a fee'}</Heading>}
      body={
        <Box ref={topRef} css={{ paddingBlock: 'sm' }} data-testid="MultifamilyCostsAndFeesModal">
          <Paragraph css={{ marginBottom: '400' }}>
            If a fee is missing or you can&apos;t import it from your feed, you can add it here manually. It will
            display alongside other fees on your Zillow listing.
          </Paragraph>

          {feeCreateOrUpdateError && (
            <Alert tone="critical" css={{ marginBottom: '400' }}>
              {feeCreateOrUpdateError}
            </Alert>
          )}

          <Form asChild={true} onSubmit={(e) => e.preventDefault()}>
            <div>
              {/* Category field */}
              <FeeDropdownSelectField
                value={feeCategory}
                onChange={handleFeeCategoryChange}
                dictionaryItems={data?.dictionaryItems?.feeCategory as Record<string, FeeCategoryDictionaryItemDto>}
                fieldLabel="Category"
                required={true}
                onBlur={validateField}
                errorMessage={feeErrors.feeCategory ?? ''}
                fieldName="feeCategory"
                hasIcon={true}
                formHelpText={
                  feeToEdit !== null
                    ? 'Category can’t be edited because it would override your other inputs. Add a new fee instead.'
                    : 'Select the category your fee should go in'
                }
                disabled={feeToEdit !== null}
                readOnly={feeToEdit !== null}
              />

              {/* Fee name/custom fee name field */}
              {feeCategory === 'CUSTOM' ? (
                <FeeInputField
                  onChange={setFeeName}
                  value={feeName}
                  fieldLabel="Custom fee name"
                  required={true}
                  onBlur={validateField}
                  fieldName="customFeeName"
                  errorMessage={feeErrors.customFeeName ?? ''}
                  charLimit={255}
                />
              ) : (
                <FeeDropdownSelectField
                  required={true}
                  value={feeType}
                  fieldLabel="Fee name"
                  disabled={feeCategory === ''}
                  onChange={setFeeType}
                  dictionaryItems={
                    feeCategory !== ''
                      ? (data?.dictionaryItems?.feeCategory[feeCategory as FeeTypeGrouping]?.feeType as Record<
                          string,
                          FeeDictionaryItemDisplayDto
                        >)
                      : null
                  }
                  onBlur={validateField}
                  fieldName="feeName"
                  errorMessage={feeErrors.feeName ?? ''}
                  formHelpText="Select a fee name. If you don’t see a name for the fee you need, set your category as “Custom” to add your own."
                />
              )}

              {/* Description */}
              <FeeInputField
                onChange={setFeeDescription}
                value={feeDescription}
                fieldLabel="Description"
                formHelpText="Only add a description when necessary, otherwise leave blank"
                onBlur={validateField}
                fieldName="feeDescription"
                errorMessage={feeErrors.feeDescription ?? ''}
                charLimit={1023}
              />

              {/* Payment frequency */}
              <FeeDropdownSelectField
                required={true}
                value={feePaymentFrequencyType}
                onChange={setFeePaymentFrequencyType}
                dictionaryItems={
                  data?.dictionaryItems?.feePaymentFrequencyType as Record<string, FeeDictionaryItemDisplayDto>
                }
                fieldLabel="Payment frequency"
                onBlur={validateField}
                fieldName="feePaymentFrequencyType"
                formHelpText="Select how often this fee should be paid"
                errorMessage={feeErrors.feePaymentFrequencyType ?? ''}
                feePaymentFrequencyCategory={
                  data?.dictionaryItems?.feePaymentFrequencyCategory as Record<
                    string,
                    FeePaymentFrequencyDictionaryItemDto
                  >
                }
              />

              {/* Max occurrences */}
              <MaxOccurrencesField
                value={maxOccurrences}
                onChange={setMaxOccurrences}
                fieldLabel="Max occurrences"
                formHelpText="Select the maximum number of times this fee can be charged. This field is optional."
                onBlur={validateField}
                fieldName="maxOccurrences"
                errorMessage={feeErrors.maxOccurrences ?? ''}
              />

              {/* Required */}
              <FeeRadioGroupField
                orientation="vertical"
                value={feeRequirementType}
                onChange={setFeeRequirementType}
                enums={data?.dictionaryItems?.feeRequirementType as Record<string, FeeDictionaryItemDisplayDto>}
                label="What are the payment expectations?"
                required={true}
              />

              {/* Refundable */}
              <FeeRadioGroupField
                orientation="vertical"
                value={feeRefundableType}
                onChange={setFeeRefundableType}
                enums={data?.dictionaryItems?.feeRefundableType as Record<string, FeeDictionaryItemDisplayDto>}
                label="Is this amount refundable?"
                required={false}
              />

              {/* Level */}
              <FeeRadioGroupField
                orientation="vertical"
                value={feeApplicableLevelType}
                onChange={handleFeeApplicableLevelTypeChange}
                enums={feeApplicableLevelEnums}
                label="What does this fee apply to?"
                required={true}
                tooltipContent={feeLevelApplicableTooltipContent}
              />

              {/* Included units/floorplans */}
              {feeApplicableLevelType === 'UNIT' && (
                <FeeComboboxField
                  required={true}
                  fieldLabel="Included units"
                  value={units}
                  onChange={setUnits}
                  dictionaryItems={data?.dictionaryItems?.unit as Record<string, ModelDictionaryItemDto>}
                />
              )}
              {feeApplicableLevelType === 'FLOOR_PLAN' && (
                <FeeComboboxField
                  required={true}
                  fieldLabel="Included floor plans"
                  value={floorplans}
                  onChange={setFloorplans}
                  dictionaryItems={data?.dictionaryItems?.floorplan as Record<string, ModelDictionaryItemDto>}
                />
              )}

              {/* Fee format */}
              <FeeDropdownSelectField
                value={feeFormat}
                onChange={handleFeeFormatChange}
                dictionaryItems={feeCalculationTypes}
                fieldLabel="Fee format"
                required={true}
                onBlur={validateField}
                fieldName="feeFormat"
                errorMessage={feeErrors.feeFormat ?? ''}
                formHelpText="Select how the fee amount should be shown. Ex: A flat fee of $50, or a range of $25–$45."
              />

              {shouldRenderInlineStaticAlert && (
                <InlineStaticAlert
                  marginBottom="600"
                  bodyText={
                    feeFormat === FeeCalculationType.VARIABLE_USAGE_BASED
                      ? `This fee will not be included in your listing’s total monthly price since it’s based on usage`
                      : `This fee will be included in your listing’s total monthly price`
                  }
                />
              )}

              {/*  Fee amount - 1 or 2 inputs depending if fee format is a range, or stepped fee amounts */}
              {feeFormat === FeeCalculationType.STEPPED ? (
                <SteppedFeeField
                  steppedFeeAmounts={steppedFeeAmounts}
                  onSteppedFeeAmountsChange={setSteppedFeeAmounts}
                  onStepRemoved={handleStepRemoved}
                  onBlur={validateField}
                  feeErrors={feeErrors}
                />
              ) : (
                <FeeAmountField
                  setFeeAmountMin={setFeeAmountMin}
                  setFeeAmountMax={setFeeAmountMax}
                  valueMin={feeAmountMin}
                  valueMax={feeAmountMax}
                  feeFormat={
                    (data?.dictionaryItems?.feeCalculationType[
                      feeFormat as FeeCalculationType
                    ] as FeeCalculationDictionaryItemDisplayDto) ??
                    ('FIXED' as unknown as FeeCalculationDictionaryItemDisplayDto)
                  }
                  feeFormatName={feeFormat as FeeCalculationType}
                  onBlur={validateField}
                  feeErrors={feeErrors}
                />
              )}
            </div>
          </Form>
        </Box>
      }
      footer={
        <ModalFooter
          submitText="Save"
          onSubmit={onSubmit}
          onSubmitDataEventId={EVENT_IDS.CLICK_SAVE_FEE}
          onCancel={handleCancelClick}
          onCancelDataEventId={EVENT_IDS.CLICK_CANCEL_FEE_EDIT}
          isSubmitting={isCreatingFee || isUpdatingFee}
          isSubmitDisabled={!canSubmit}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{
        escapeKey: true,
        outsidePress: false,
      }}
      data-testid="MultifamilyCostsAndFeesModal"
    />
  );
};

export default MultifamilyCostsAndFeesModal;
